import { DateTime } from 'luxon';
import { userApi } from './index';
import { IUserConnectedServices } from '../../../models/Accounts';
import { AuthCredentialsResponse } from '../../../models/AuthCredentials';
import {
  IUserInfo,
  IUserBanner,
  IUserDocument,
  IUserRepairWorks,
  IAppSettings,
  IMultiAccounts,
  ILinkAccount,
  IPushSettings,
  IUserInfoTransform,
  IInvite,
} from '../../../models/UserInfo';
import { TerminationSend } from '../../../models/ContractTermination';
import { config } from '../config';
import { share50PercentId } from '../../../constants/configuration';
import { DateVOv2 } from '../../../core/DateVOv2';

export const userInfoApi = userApi.injectEndpoints({
  endpoints: (build) => ({
    getUserRepairWorks: build.query<IUserRepairWorks[], void>({
      query: () => ({
        url: config.USER_INFO.REPAIR_WORKS,
      }),
      providesTags: () => ['RepairWorks'],
    }),
    getUserBanners: build.query<IUserBanner[], void>({
      query: () => ({
        url: config.USER_INFO.BANNERS,
      }),
      providesTags: () => ['UserBanners'],
    }),
    getUserInfo: build.query<IUserInfo, string>({
      query: () => ({
        url: config.USER_INFO.INFO,
      }),
      providesTags: () => ['UserInfo'],
    }),
    getUserInfoMultiAcc: build.query<IUserInfo, { subAccountId: number | undefined }>({
      query: ({ subAccountId }) => ({
        url: config.USER_INFO.INFO,
        params: {
          ...(subAccountId && { subAccountId }),
        },
      }),
      providesTags: () => ['UserInfo'],
    }),
    getUserInfoMultiAccWithTransformResponse: build.query<IUserInfoTransform, { subAccountId: number | undefined }>({
      query: ({ subAccountId }) => ({
        url: config.USER_INFO.INFO,
        params: {
          ...(subAccountId && { subAccountId }),
        },
      }),
      transformResponse: (response: IUserInfo): IUserInfoTransform => {
        const today = new DateVOv2().getLuxonKiev();

        const share50Percent = response.Shares.Activated.find((i) => {
          const expiresAt = DateTime.fromSQL(i.ActiveTo).setZone('Europe/Kiev');

          return i.id === share50PercentId && expiresAt > today;
        });
        const transformResponse = { ...response, сonnectedShare50Percent: share50Percent || null };

        return transformResponse;
      },
      providesTags: () => ['UserInfo'],
    }),
    getPushSettings: build.query<IPushSettings, string>({
      query: () => ({
        url: config.USER_INFO.PUSHS_SETTINGS,
      }),
      providesTags: () => ['PushSettings'],
    }),
    getConnectedAccounts: build.query<IUserConnectedServices, string>({
      query: () => ({
        url: config.USER_INFO.CONNECTED_SERVICES,
      }),
      providesTags: () => ['Accounts'],
    }),
    getAppSettings: build.query<IAppSettings, string>({
      query: () => ({
        url: config.USER_INFO.GET_APP_SETTINGS,
      }),
      keepUnusedDataFor: 0,
    }),
    getAuthTokenBrizTv: build.query<{ code: string }, void>({
      query: () => ({
        url: config.USER_INFO.GET_AUTH_TOKEN_BRIZ_TV,
      }),
      keepUnusedDataFor: 0,
    }),
    getMultiaccountMembers: build.query<IMultiAccounts, string>({
      query: () => ({
        url: config.USER_INFO.MULTIACCOUNT_MEMBERS,
      }),
      providesTags: () => ['MultiAccount'],
    }),
    getRecommendedTopUpAmount: build.query<{ amount: number }, void>({
      query: () => ({
        url: config.USER_INFO.RECOMMENDED_TOP_UP_AMOUNT,
      }),
      keepUnusedDataFor: 0,
    }),
    changeMembersPriorities: build.mutation<{ status: boolean }, { userIds: number[] }>({
      query: ({ userIds }) => ({
        url: `${config.USER_INFO.MULTIACCOUNT_MEMBERS}/priorities`,
        method: 'POST',
        body: {
          userIds,
        },
      }),
      invalidatesTags: ['MultiAccount'],
    }),
    linkFamilyAccount: build.mutation<ILinkAccount, { userId: string; password: string }>({
      query: ({ userId, password }) => ({
        url: config.USER_INFO.MULTIACCOUNT_MEMBERS,
        method: 'POST',
        body: {
          userId,
          password,
        },
      }),
      invalidatesTags: ['MultiAccount'],
    }),
    resendMemberInvite: build.mutation<IInvite, { userId: number }>({
      query: ({ userId }) => ({
        url: `${config.USER_INFO.MULTIACCOUNT_MEMBERS}/invite`,
        method: 'POST',
        body: {
          userId,
        },
      }),
    }),
    verifyMemberAccount: build.mutation<{ status: boolean }, { code: string }>({
      query: ({ code }) => ({
        url: `${config.USER_INFO.MULTIACCOUNT_MEMBERS}/verify`,
        method: 'POST',
        body: {
          code,
        },
      }),
    }),
    joinMemberAccount: build.mutation<{ status: boolean }, void>({
      query: () => ({
        url: config.USER_INFO.MULTIACCOUNT_JOIN,
        method: 'POST',
      }),
      invalidatesTags: ['MultiAccount'],
    }),
    changeMemberAccount: build.mutation<ILinkAccount, { UserID: number; useAutoPay?: boolean; alias?: string | null }>({
      query: ({ UserID, useAutoPay, alias }) => ({
        url: `${config.USER_INFO.MULTIACCOUNT_MEMBERS}/${UserID}`,
        method: 'PATCH',
        body: {
          ...(typeof useAutoPay !== 'undefined' && { useAutoPay }),
          ...(typeof alias !== 'undefined' && { alias }),
        },
      }),
      invalidatesTags: ['MultiAccount'],
    }),
    deleteMemberAccount: build.mutation<{ status: boolean }, { userId: number }>({
      query: ({ userId }) => ({
        url: `${config.USER_INFO.MULTIACCOUNT_MEMBERS}/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MultiAccount'],
    }),
    getUserDocuments: build.query<IUserDocument[], string>({
      query: () => ({
        url: config.USER_INFO.DOCUMENTS,
      }),
    }),
    downloadUserDocument: build.query<Blob, string>({
      query: (docType) => ({
        url: `${config.USER_INFO.DOWNLOAD_DOCUMENT}/${docType}`,
        headers: { 'Content-Type': 'application/pdf' },
        responseHandler: (response) => {
          return response.status === 200 ? response.blob() : response.json();
        },
      }),
    }),
    createUserDocument: build.mutation<AuthCredentialsResponse, TerminationSend>({
      query: ({ body, docType }) => ({
        url: `${config.USER_INFO.CREATE_DOCUMENT}/${docType}`,
        method: 'POST',
        body,
      }),
    }),
    showUserDocument: build.query<Blob, string>({
      query: (docType) => ({
        url: `${config.USER_INFO.SHOW_DOCUMENT}/${docType}`,
        headers: { 'Content-Type': 'application/pdf' },
        responseHandler: (response) => {
          return response.status === 200 ? response.blob() : response.json();
        },
      }),
    }),
    setNewAccountPassword: build.mutation<
      { status: boolean },
      { password: string; passwordConfirmation: string; currentPassword: string }
    >({
      query: ({ password, passwordConfirmation, currentPassword }) => ({
        url: config.USER_INFO.SET_NEW_ACCOUNT_PASSWORD,
        method: 'PUT',
        body: {
          Password: password,
          Password_confirmation: passwordConfirmation,
          currentPassword,
        },
      }),
    }),
    setEmailNotification: build.mutation<{ status: boolean }, { isNotification: string }>({
      query: ({ isNotification }) => ({
        url: config.USER_INFO.SET_EMAIL_NOTIFICATION,
        method: 'POST',
        body: {
          Accept: isNotification,
          NoBalance: isNotification,
          News: isNotification,
          Repairs: isNotification,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    setSmsNotification: build.mutation<{ status: boolean }, { isNotification: boolean }>({
      query: ({ isNotification }) => ({
        url: config.USER_INFO.SET_SMS_NOTIFICATION,
        method: 'PUT',
        body: {
          accept: isNotification,
          noBalance: isNotification,
          news: isNotification,
          repairs: isNotification,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    setPushNotification: build.mutation<{ status: boolean }, { isNotification: boolean }>({
      query: ({ isNotification }) => ({
        url: config.USER_INFO.PUSHS_SETTINGS,
        method: 'PUT',
        body: {
          Accept: isNotification,
          NoBalance: isNotification,
          News: isNotification,
          Repairs: isNotification,
        },
      }),
      invalidatesTags: ['PushSettings'],
    }),
    editEmail: build.mutation<{ status: boolean }, { editedEmail: string; currentPassword: string }>({
      query: ({ editedEmail, currentPassword }) => ({
        url: config.USER_INFO.ADD_OR_EDIT_EMAIL,
        method: 'PUT',
        body: {
          email: editedEmail,
          currentPassword,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    addNewEmail: build.mutation<{ status: boolean }, { newEmail: string }>({
      query: ({ newEmail }) => ({
        url: config.USER_INFO.ADD_EMAIL,
        method: 'POST',
        body: {
          email: newEmail,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    deleteEmail: build.mutation<{ success: boolean }, { currentPassword: string }>({
      query: ({ currentPassword }) => ({
        url: config.USER_INFO.DELETE_EMAIL,
        method: 'DELETE',
        body: {
          currentPassword,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    confirmEmail: build.mutation<{ status: boolean }, { code: string }>({
      query: ({ code }) => ({
        url: config.USER_INFO.CONFIRM_EMAIL,
        method: 'POST',
        body: {
          code,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    resendEmail: build.mutation<{ status: boolean }, void>({
      query: () => ({
        url: config.USER_INFO.RESEND_EMAIL,
        method: 'POST',
      }),
      invalidatesTags: ['UserInfo'],
    }),
    addBackupPhone: build.mutation<{ status: boolean }, { phone: string }>({
      query: ({ phone }) => ({
        url: config.USER_INFO.CHANGE_BACKUP_PHONE,
        method: 'POST',
        body: {
          phone,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    editBackupPhone: build.mutation<{ status: boolean }, { phone: string; phoneId: number }>({
      query: ({ phone, phoneId }) => ({
        url: `${config.USER_INFO.CHANGE_BACKUP_PHONE}/${phoneId}`,
        method: 'PATCH',
        body: {
          phone,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    deleteBackupPhone: build.mutation<{ status: boolean }, { phoneId: number }>({
      query: ({ phoneId }) => ({
        url: `${config.USER_INFO.CHANGE_BACKUP_PHONE}/${phoneId}`,
        method: 'DELETE',
        body: {},
      }),
      invalidatesTags: ['UserInfo'],
    }),
    addNewSmsPhone: build.mutation<{ status: boolean }, { phone: string }>({
      query: ({ phone }) => ({
        url: config.USER_INFO.ADD_SMS_PHONE,
        method: 'POST',
        body: {
          phone,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    requestConfirmationSmsCode: build.mutation<{ status: boolean }, { phone: string }>({
      query: ({ phone }) => ({
        url: config.USER_INFO.REQUEST_CONFIRMATION_SMS_CODE_NEW,
        method: 'POST',
        body: {
          phone,
        },
      }),
    }),
    requestConfirmationSmsCodeIsEdit: build.mutation<{ status: boolean }, { phone: string; currentPassword: string }>({
      query: ({ phone, currentPassword }) => ({
        url: config.USER_INFO.REQUEST_CONFIRMATION_SMS_CODE_NEW_EDIT,
        method: 'POST',
        body: {
          phone,
          currentPassword,
        },
      }),
    }),
    checkConfirmationSmsCode: build.mutation<{ status: boolean }, { phone: string; code: number }>({
      query: ({ phone, code }) => ({
        url: config.USER_INFO.CHECK_CONFIRMATION_SMS_CODE,
        method: 'POST',
        body: {
          phone,
          code,
        },
      }),
    }),
    deleteSmsPhone: build.mutation<{ status: boolean }, { currentPassword: string }>({
      query: ({ currentPassword }) => ({
        url: config.USER_INFO.EDIT_SMS_PHONE,
        method: 'DELETE',
        body: {
          currentPassword,
        },
      }),
      invalidatesTags: ['UserInfo'],
    }),
    changeUserServiceLanguage: build.mutation<{ success: boolean }, { lang: string }>({
      query: ({ lang }) => ({
        url: config.USER_INFO.CHANGE_USER_SERVICE_LANGUAGE,
        method: 'PUT',
        body: {
          lang,
        },
      }),
      invalidatesTags: ['Accounts', 'History', 'Preset', 'UserInfo', 'UserBanners', 'RepairWorks'],
    }),
  }),
});
